import React from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Grid,
  Divider,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { MatchData } from '@/interfaces/liveGamesType';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/context/AuthContext';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { FiTrendingUp, FiCalendar } from 'react-icons/fi';
import { getHeadToHead } from '../../services/api';

interface HistoricalTrendsProps {
  match: MatchData;
  isLoading?: boolean;
}

const HistoricalTrends: React.FC<HistoricalTrendsProps> = ({ match, isLoading: propIsLoading = false }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const { isAuthenticated } = useAuth();

  // Buscar histórico de confrontos diretos
  const {
    data: h2hData,
    isLoading: h2hLoading
  } = useQuery({
    queryKey: ['headToHead', match?.match_hometeam_id, match?.match_awayteam_id],
    queryFn: () => getHeadToHead(match.match_hometeam_id, match.match_awayteam_id, isAuthenticated),
    enabled: !!match?.match_hometeam_id && !!match?.match_awayteam_id,
    staleTime: 3600000, // 1 hora
  });

  // Verificar se está carregando
  const isLoading = propIsLoading || h2hLoading;

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={300}>
        <CircularProgress size={30} />
      </Box>
    );
  }

  // Dados simulados para o gráfico de tendências
  const trendData = [
    { name: '6 jogos atrás', home: 2, away: 1, total: 3 },
    { name: '5 jogos atrás', home: 1, away: 0, total: 1 },
    { name: '4 jogos atrás', home: 3, away: 2, total: 5 },
    { name: '3 jogos atrás', home: 2, away: 2, total: 4 },
    { name: '2 jogos atrás', home: 1, away: 1, total: 2 },
    { name: 'Último jogo', home: 2, away: 0, total: 2 },
  ];

  // Dados simulados para a tabela de confrontos diretos
  const h2hMatches = [
    { date: '2023-05-15', home: match.match_hometeam_name, away: match.match_awayteam_name, score: '2-0', competition: 'Liga Nacional' },
    { date: '2022-11-20', home: match.match_awayteam_name, away: match.match_hometeam_name, score: '1-1', competition: 'Copa Nacional' },
    { date: '2022-03-05', home: match.match_hometeam_name, away: match.match_awayteam_name, score: '1-0', competition: 'Liga Nacional' },
    { date: '2021-09-12', home: match.match_awayteam_name, away: match.match_hometeam_name, score: '2-2', competition: 'Liga Nacional' },
  ];

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
        <FiTrendingUp style={{ marginRight: 8 }} /> Tendências Históricas
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Box sx={{ height: 250, mt: 2 }}>
            <Typography variant="body2" gutterBottom align="center">
              Gols por Partida (Últimos 6 Jogos)
            </Typography>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={trendData}
                margin={{ top: 10, right: 10, left: 0, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis />
                <Tooltip
                  contentStyle={{
                    backgroundColor: isDarkMode ? '#333' : '#fff',
                    border: `1px solid ${theme.palette.divider}`
                  }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="home"
                  name={match.match_hometeam_name}
                  stroke={theme.palette.primary.main}
                  activeDot={{ r: 8 }}
                />
                <Line
                  type="monotone"
                  dataKey="away"
                  name={match.match_awayteam_name}
                  stroke={theme.palette.error.main}
                  activeDot={{ r: 8 }}
                />
                <Line
                  type="monotone"
                  dataKey="total"
                  name="Total de Gols"
                  stroke={theme.palette.success.main}
                  activeDot={{ r: 8 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" gutterBottom align="center" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FiCalendar style={{ marginRight: 8 }} /> Confrontos Diretos
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 250 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Data</TableCell>
                    <TableCell>Partida</TableCell>
                    <TableCell align="center">Placar</TableCell>
                    <TableCell>Competição</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {h2hMatches.map((h2h, index) => (
                    <TableRow key={`h2h-${index}`}>
                      <TableCell>{h2h.date}</TableCell>
                      <TableCell>{h2h.home} vs {h2h.away}</TableCell>
                      <TableCell align="center">{h2h.score}</TableCell>
                      <TableCell>{h2h.competition}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap' }}>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Typography variant="body2" color="textSecondary">Vitórias {match.match_hometeam_name}</Typography>
              <Typography variant="h6" color="primary.main">3</Typography>
            </Box>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Typography variant="body2" color="textSecondary">Empates</Typography>
              <Typography variant="h6">2</Typography>
            </Box>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Typography variant="body2" color="textSecondary">Vitórias {match.match_awayteam_name}</Typography>
              <Typography variant="h6" color="error.main">1</Typography>
            </Box>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Typography variant="body2" color="textSecondary">Média de Gols</Typography>
              <Typography variant="h6" color="success.main">2.8</Typography>
            </Box>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Typography variant="body2" color="textSecondary">Ambas Marcaram</Typography>
              <Typography variant="h6">67%</Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default HistoricalTrends;